import { useState, useCallback, useMemo } from 'react';
import { Box, Chip } from '@material-ui/core';
import { SButton, SETVIImage, SBase, SText } from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';
import { useQuoteStore } from 'pages/quotes/store';
import { enqueueSnackbar } from 'notistack';
import moment from 'moment';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
// Step components
import { CheckIconV2, InfoIconV2 } from '@setvi/shared/components/sicons';
import {
  SingerData,
  IweckData,
  FranklinData,
  BoelterData
} from '@setvi/shared/enums';
import { COMPANIES } from 'Enums/Companies';
import { useMutation } from '@tanstack/react-query';
import {
  QuotesQueryKey,
  updateQuotePreviewInfoMutation,
  updateQuoteUserPhotoMutation
} from 'Services';
import {
  DBEventType,
  useDatabricksAnalytics,
  useSubscribedMutation
} from '@setvi/shared/hooks';
import { useParams } from 'react-router-dom';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { PreparedForStep } from './steps/prepared-for';
import { PreparedByStep } from './steps/prepared-by';
import { ExpirationDateStep } from './steps/expiration-date';
import { OverallNoteStep } from './steps/overall-note';
import {
  Content,
  ContentContainer,
  DismissBtn,
  Footer,
  StepContent,
  StepItem,
  StepLabel,
  StepperContainer,
  WarningContainer
} from './style';
import {
  Warning,
  WarningContent,
  WarningIconBox
} from '../../pdf-preview-tab/styled';

export const QUOTE_DETAILS_DIALOG = 'quote-details-dialog';

const isValidEmail = (email: string) => {
  if (!email) return false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const getCompanyData = (companyId: number) => {
  switch (companyId) {
    case COMPANIES.IWECK_DEV:
      return { name: IweckData.Name, address: IweckData.Address };
    case COMPANIES.FRANLINK_DEV:
      return { name: FranklinData.Name, address: FranklinData.Address };
    case COMPANIES.BOELTER_DEV:
      return { name: BoelterData.Name, address: BoelterData.Address };
    case COMPANIES.SINGER_DEV:
    case COMPANIES.SINGER_PROD:
    default:
      return { name: SingerData.Name, address: SingerData.Address };
  }
};

interface Step {
  id: string;
  label: string;
  icon: string;
  completed: boolean;
  hasError?: boolean;
}

interface QuoteDetailsModalProps {
  onClose: () => void;
}

export const QuoteDetailsModal = ({ onClose }: QuoteDetailsModalProps) => {
  const { id: rfqId } = useParams();
  const { closeDialog } = useDialog();
  const { user } = useAppContext();
  const { quote, updateQuoteInfo, setAutoSaveEnabled } = useQuoteStore();
  const { captureDBAnalytics } = useDatabricksAnalytics();

  const [activeStep, setActiveStep] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const companyData = getCompanyData(user?.CompanyID);
  const [formData, setFormData] = useState({
    preparedFor: {
      companyName: quote?.previewInfo?.preparedFor?.companyName || '',
      address: quote?.previewInfo?.preparedFor?.companyAddress || '',
      phoneNumber: quote?.previewInfo?.preparedFor?.companyPhoneNumber || ''
    },
    preparedBy: {
      file: null,
      salesRepName: quote?.previewInfo?.preparedBy?.fullName || '',
      contactNumber: quote?.previewInfo?.preparedBy?.phoneNumber || '',
      email: quote?.previewInfo?.preparedBy?.email || '',
      photo: quote?.previewInfo?.preparedBy?.photo || '',
      companyName: companyData.name,
      companyAddress: companyData.address
    },
    expirationDate: quote?.validUntil
      ? new Date(quote.validUntil)
      : moment().add(30, 'days').toDate(),
    overallNote: quote?.customerNote || ''
  });

  const { mutateAsync: updateQuote, isLoading: isPreviewLoading } =
    useSubscribedMutation(updateQuotePreviewInfoMutation(), [
      QuotesQueryKey.GET_PREVIEW
    ]);

  const { mutateAsync: updatePhoto, isLoading: isImageLoading } = useMutation(
    updateQuoteUserPhotoMutation()
  );

  const steps: Step[] = useMemo(
    () => [
      {
        id: 'prepared-for',
        label: 'Prepared for',
        icon: '/icons/quotes-prepared-for.svg',
        completed:
          !!formData.preparedFor.companyName &&
          !!formData.preparedFor.address &&
          !!formData?.preparedFor?.phoneNumber,
        hasError:
          !formData.preparedFor.companyName ||
          !formData.preparedFor.address ||
          !formData?.preparedFor?.phoneNumber
      },
      {
        id: 'prepared-by',
        label: 'Prepared by',
        icon: '/icons/quotes-prepared-by.svg',
        completed:
          !!formData.preparedBy.salesRepName &&
          isValidEmail(formData.preparedBy.email) &&
          !!formData.preparedBy.contactNumber,
        hasError:
          !formData.preparedBy.salesRepName ||
          !isValidEmail(formData.preparedBy.email) ||
          !formData.preparedBy.contactNumber
      },
      {
        id: 'expiration-date',
        label: 'Expiration date',
        icon: '/icons/quotes-expiration-date.svg',
        completed: !!formData.expirationDate,
        hasError: false
      },
      {
        id: 'overall-note',
        label: 'Overall Note',
        icon: '/icons/quotes-overall-note.svg',
        completed: true,
        hasError: false
      }
    ],
    [formData]
  );

  const handleStepClick = useCallback((index: number) => {
    setActiveStep(index);
  }, []);

  const handleSave = useCallback(async () => {
    const { preparedBy, preparedFor } = formData;
    const hasIncompleted = steps?.some(s => !s?.completed);

    if (hasIncompleted) {
      setShowWarning(true);
      return;
    }

    setAutoSaveEnabled(false);

    if (preparedBy?.file) {
      try {
        const res = await updatePhoto({
          id: quote?._id,
          image: preparedBy?.file
        });

        if (!res) return;

        preparedBy.photo = res?.previewInfo?.preparedBy?.photo || '';
      } catch (error) {
        enqueueSnackbar('Failed to update sales rep photo', {
          variant: 'error'
        });
      }
    }

    const body = {
      previewInfo: {
        preparedFor: {
          companyPhoneNumber: preparedFor.phoneNumber,
          companyAddress: preparedFor.address,
          companyName: preparedFor?.companyName
        },
        preparedBy: {
          email: preparedBy?.email,
          phoneNumber: preparedBy?.contactNumber,
          fullName: preparedBy?.salesRepName,
          photo: preparedBy?.photo
        }
      }
    };

    const mainBody = {
      validUntil: moment(formData.expirationDate).format('YYYY-MM-DD'),
      customerNote: formData.overallNote
    };

    const res = await updateQuote({
      id: quote?._id,
      ...body.previewInfo,
      ...mainBody
    });

    // Capture Databricks Analytics
    captureDBAnalytics({
      eventType: DBEventType.QuotesModule,
      data: {
        rfqId,
        quoteId: quote?._id,
        action: 'updateQuoteDetails',
        ...(res?.error && { error: res?.error }),
        body: {
          ...body.previewInfo,
          ...mainBody
        },
        source: {
          name: 'RFQDetails'
        }
      }
    });

    if (!res) return;

    updateQuoteInfo({ ...body, ...mainBody });

    closeDialog(QUOTE_DETAILS_DIALOG);

    onClose();
  }, [
    captureDBAnalytics,
    closeDialog,
    formData,
    onClose,
    quote?._id,
    rfqId,
    setAutoSaveEnabled,
    steps,
    updatePhoto,
    updateQuote,
    updateQuoteInfo
  ]);

  const handleCancel = useCallback(() => {
    closeDialog(QUOTE_DETAILS_DIALOG);
    onClose();
  }, [closeDialog, onClose]);

  const updateFormData = useCallback((stepId: string, data: any) => {
    setFormData(prev => ({
      ...prev,
      [stepId]: data
    }));
  }, []);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <PreparedForStep
            data={formData.preparedFor}
            onChange={data => updateFormData('preparedFor', data)}
          />
        );
      case 1:
        return (
          <PreparedByStep
            data={formData.preparedBy}
            onChange={data => updateFormData('preparedBy', data)}
            onImageUpload={(file: File) =>
              setFormData(prev => ({
                ...prev,
                preparedBy: {
                  ...prev?.preparedBy,
                  file
                }
              }))
            }
            emailError={
              formData.preparedBy.email &&
              !isValidEmail(formData.preparedBy.email)
                ? 'Please enter a valid email address'
                : ''
            }
          />
        );
      case 2:
        return (
          <ExpirationDateStep
            date={formData.expirationDate}
            onChange={date => updateFormData('expirationDate', date)}
          />
        );
      case 3:
        return (
          <OverallNoteStep
            note={formData.overallNote}
            onChange={note => updateFormData('overallNote', note)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <SBase
      disablePadding
      width="70vw"
      height="50vw"
      maxHeight={800}
      maxWidth={1200}
      header={{
        title: 'Edit Quote Details',
        style: {
          background: 'white'
        }
      }}>
      <ContentContainer>
        <Box display="flex" height="100%" overflow="hidden" position="relative">
          <StepperContainer>
            {steps.map((step, index) => {
              const isActive = activeStep === index;
              return (
                <StepItem
                  key={step.id}
                  onClick={() => handleStepClick(index)}
                  style={{
                    borderLeft: isActive
                      ? '8px solid #1E96FC'
                      : '8px solid transparent'
                  }}>
                  <SETVIImage
                    src={step.icon}
                    alt="step icon"
                    width={24}
                    height={24}
                  />

                  <StepContent>
                    <StepLabel
                      style={{
                        fontWeight: isActive ? 600 : 400
                      }}>
                      {step.label}
                    </StepLabel>
                    {!step?.completed && (
                      <Chip
                        label="Missing data"
                        style={{
                          color: '#FA2020',
                          borderRadius: 120,
                          backgroundColor: '#FED2D2',
                          fontWeight: 'bold',
                          height: '24px'
                        }}
                      />
                    )}
                  </StepContent>
                </StepItem>
              );
            })}
          </StepperContainer>

          <Content>{renderStepContent()}</Content>

          {showWarning && (
            <WarningContainer>
              <Warning
                style={{
                  width: '95%'
                }}>
                <WarningContent>
                  <WarningIconBox>
                    <InfoIconV2
                      fill="white"
                      style={{
                        transform: 'rotate(180deg)'
                      }}
                    />
                  </WarningIconBox>
                  <SText
                    title="Please make sure all required information is filled in before you continue."
                    fontColor="#FA2020"
                  />
                </WarningContent>

                <DismissBtn
                  onClick={() => setShowWarning(false)}
                  variant="text"
                  color="default"
                  startIcon={
                    <CloseRoundedIcon
                      style={{
                        marginBottom: 3
                      }}
                    />
                  }>
                  Dismiss
                </DismissBtn>
              </Warning>
            </WarningContainer>
          )}
        </Box>

        <Footer>
          <SButton
            style={{ borderRadius: 120, minHeight: 40 }}
            variant="outlined"
            color="inherit"
            disabled={isImageLoading || isPreviewLoading}
            onClick={handleCancel}>
            Cancel
          </SButton>
          <SButton
            style={{ borderRadius: 120, minHeight: 40 }}
            variant="contained"
            loading={isImageLoading || isPreviewLoading}
            disabled={isImageLoading || isPreviewLoading}
            startIcon={<CheckIconV2 />}
            onClick={handleSave}>
            Save Changes
          </SButton>
        </Footer>
      </ContentContainer>
    </SBase>
  );
};
